import config from "../config";
import axios from "axios";
import { toast } from "react-hot-toast";
import i18n from "../i18n/config";
import Cookies from "js-cookie";

let activeRequests = 0;
let toastId = null;
let isForceLogoutInProgress = false;

// 🚫 Do NOT import the file that registers global interceptors.
// ✅ Private instance — no global interceptors attached.
const http = axios.create({
  baseURL: config.API_URL,
  // Always resolve; you'll handle 4xx/5xx yourself
  validateStatus: () => true,
});

function authHeader() {
  const raw = localStorage.getItem("user");
  let token = null;
  try {
    const u = JSON.parse(raw);
    token = u?.token || u?.accessToken || u?.access_token || u;
  } catch {
    token = raw;
  }
  const normalizedToken = String(token || "")
    .trim()
    .replace(/^Bearer\s+/i, "")
    .replace(/^"+|"+$/g, "");
  return normalizedToken ? { Authorization: `Bearer ${normalizedToken}` } : {};
}

function extractMessage(payload, fallback = "Request failed") {
  if (!payload) return fallback;
  if (typeof payload === "string") return payload;
  if (payload.message)
    return Array.isArray(payload.message)
      ? payload.message.join(" | ")
      : payload.message;
  if (typeof payload.error === "string") return payload.error;
  if (typeof payload.detail === "string") return payload.detail;
  try {
    return JSON.stringify(payload);
  } catch {
    return fallback;
  }
}

function shouldForceLogout(path) {
  const p = String(path || "").toLowerCase();
  // Login endpoint can return 401 for wrong credentials, which should not redirect.
  return p !== "/auth/login";
}

function classifyLogoutReason(message) {
  const m = String(message || "").toLowerCase();
  if (m.includes("another device") || m.includes("device/session")) {
    return "new_device";
  }
  if (m.includes("inactivity") || m.includes("idle")) {
    return "idle_timeout";
  }
  return "session_invalid";
}

function forceLogout(reason = "session_invalid") {
  if (typeof window === "undefined" || isForceLogoutInProgress) return;
  isForceLogoutInProgress = true;

  sessionStorage.setItem("forced_logout_reason", reason);
  sessionStorage.setItem("forced_logout_at", new Date().toISOString());

  localStorage.removeItem("user");
  localStorage.removeItem("userId");
  localStorage.removeItem("privileges");
  localStorage.removeItem("type");
  localStorage.removeItem("studentId");
  localStorage.removeItem("corporateId");
  Cookies.remove("userType");
  Cookies.remove("privileges");

  if (!window.location.pathname.includes("/login")) {
    window.location.assign("/login");
    return;
  }

  setTimeout(() => {
    isForceLogoutInProgress = false;
  }, 500);
}

export default async function apiRequest(
  type,
  path,
  data = {},
  configOverrides = {}
) {
  const url = path; // baseURL already set
  // Extract non-axios options before spreading into axios config
  const {
    suppressErrorToast = false,
    headers: overrideHeaders = {},
    ...axiosOverrides
  } = configOverrides;

  try {
    if (!type || !path) throw new Error("type/path undefined");

    activeRequests++;
    if (activeRequests === 1) toastId = toast.loading("");

    const headers = { ...authHeader(), ...overrideHeaders };

    // GET should use params; others use data
    const isGetLike = ["get", "delete", "head", "options"].includes(
      String(type).toLowerCase()
    );
    const res = await http.request({
      method: type,
      url,
      ...(isGetLike ? { params: data } : { data }),
      headers,
      ...axiosOverrides,
    });

    console.log(`[API ${type.toUpperCase()}] ${path} → ${res.status}`);

    // Uniform handling for non-2xx:
    if (res.status >= 400) {
      const msg = extractMessage(res.data, `HTTP ${res.status}`);
      if (res.status === 401 && shouldForceLogout(path)) {
        forceLogout(classifyLogoutReason(msg));
      }
      // UX — skip auto-toast when the caller wants to handle the message itself
      if (!suppressErrorToast) {
        const t = i18n.t.bind(i18n);
        const localizedMessage = msg ? t(msg, { defaultValue: msg }) : msg;
        if (res.status === 409)
          toast.error(localizedMessage || t("Conflict: booking violates current rules."));
        else if (res.status === 422)
          toast.error(localizedMessage || t("Validation failed."));
        else if (res.status === 401)
          toast.error(t("Unauthorized — please sign in again."));
        else toast.error(localizedMessage);
      }

      const rich = new Error(msg);
      rich.status = res.status;
      rich.data = res.data;
      rich.method = type;
      rich.path = path;
      rich.url = http.defaults.baseURL + path;
      throw rich;
    }

    return res;
  } finally {
    if (activeRequests === 1 && toastId) {
      toast.dismiss(toastId);
      toastId = null;
    }
    activeRequests--;
  }
}
